import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { connect, useDispatch } from 'react-redux';
import { Dispatch, useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Section } from 'UIPackage/FormModule/components/Section';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { IKnmErknmFormJudgment } from '../MainModule/interfaces/IKnmErknmFormJudgment';
import {
  getJudgmentCancellationInfoBlockId,
  getJudgmentAgainstOfficialPersonBlockId,
} from '../MainModule/constants/knmErknmFieldsElementsIds';
import { KnmCollapse } from '../../KnmPackage/MainModule/components/KnmCollapse';
import { Row } from '../../UIPackage/FormModule/components/Row';
import { FieldBlock } from '../../UIPackage/FormModule/components/FieldBlock';
import { ErknmJudgmentAgainstInfo } from './ErknmJudgmentAgainstInfo';
import { IKnmErknmFormJudgmentAgainstInfo } from '../MainModule/interfaces/IKnmErknmFormJudgmentAgainstInfo';
import { IKnmErknmForm } from '../MainModule/interfaces/IKnmErknmForm';
import { TextInputField } from '../../UIPackage/MainModule/components/TextInputField';
import { JudgmentActionsEnum } from '../../CorePackage/CatalogsModule/enums/JudgmentActionsEnum';
import { AddJudgmentModal } from './AddJudgmentModal';
import { IKnmErknmFormJudgmentActsAndDecisions } from '../MainModule/interfaces/IKnmErknmFormJudgmentActsAndDecisions';
import { ErknmJudgmentActAndDecisionInfo } from './ErknmJudgmentActAndDecisionInfo';
import { catalogGet } from '../../CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from '../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { RootActions } from '../../CorePackage/MainModule/types/RootActions';
import { CheckboxField } from '../../UIPackage/MainModule/components/CheckboxField';
import { checkHasErrorInCollapseBlock } from '../../functions/checkHasErrorInCollapseBlock';

interface IErknmJudgmentItemSubjectDataItemProps {
  cancellationActsAndDecisions: IKnmErknmFormJudgmentActsAndDecisions[] | undefined;
  isJudgmentAgainstOfficialPerson: boolean | undefined;
  isJudgmentAgainstOrganizations: boolean | undefined;
  judgmentAgainstOfficialPerson: IKnmErknmFormJudgmentAgainstInfo[] | undefined;
  judgmentAgainstOrganizations: IKnmErknmFormJudgmentAgainstInfo | undefined;
  judgmentItemName: string;
  judgmentItemSubjectDataItemId: string;
  judgmentItemSubjectDataItemName: string;
  judgmentItemValue: IKnmErknmFormJudgment;
  organizationGuid: string;
  watchOnly: boolean;
}

interface IErknmJudgmentItemSubjectDataItemStoreProps {
  formRef: IKnmFormRef | undefined;
}

const ErknmJudgmentItemSubjectDataItemWithoutStore = ({
  cancellationActsAndDecisions,
  isJudgmentAgainstOfficialPerson,
  isJudgmentAgainstOrganizations,
  judgmentAgainstOfficialPerson,
  judgmentAgainstOrganizations,
  judgmentItemName,
  judgmentItemSubjectDataItemId,
  judgmentItemSubjectDataItemName,
  judgmentItemValue,
  organizationGuid,
  watchOnly,
  formRef,
}: IErknmJudgmentItemSubjectDataItemProps & IErknmJudgmentItemSubjectDataItemStoreProps) => {
  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const openModal = () => {
    setIsModalVisible(true);
  };

  const saveModal = () => {
    setIsModalVisible(false);
  };

  const cancelModal = useCallback(() => {
    formRef?.current.change(
      `${judgmentItemSubjectDataItemName}.cancellationActsAndDecisions` as keyof IKnmErknmForm,
      cancellationActsAndDecisions,
    );
    setIsModalVisible(false);
  }, [formRef, judgmentItemSubjectDataItemName, cancellationActsAndDecisions]);

  useEffect(() => {
    dispatch(catalogGet(CommonCatalogsNamesEnum.JUDGMENT_CANCELLATION_INFORMATION));
    dispatch(catalogGet(CommonCatalogsNamesEnum.JUDGMENT_PUNISHMENT_INDEX));
  }, [dispatch]);

  const addJudgmentAgainstOfficialPerson = useCallback(() => {
    if (!watchOnly && push) {
      const newJudgmentAgainstOfficialPerson: IKnmErknmFormJudgmentAgainstInfo = {
        guid: undefined,
        officialPersonFio: undefined,
        officialPersonJobTitle: undefined,
        prosecutionType: undefined,
        punishmentType: undefined,
        amountOfPunishment: undefined,
        punishmentIndex: undefined,
        punishmentNoteLawsuits: undefined,
        cancellationInformation: undefined,
        explanation: undefined,
        isNew: true,
        listId: uuidv4(),
      };

      push(`${judgmentItemSubjectDataItemName}.judgmentAgainstOfficialPerson`, newJudgmentAgainstOfficialPerson);
    }
  }, [push, watchOnly, judgmentItemSubjectDataItemName]);
  const makeRemoveJudgmentAgainstOfficialPersonHandler = (index: number) => () =>
    !watchOnly && remove && remove(`${judgmentItemSubjectDataItemName}.judgmentAgainstOfficialPerson`, index);

  useEffect(() => {
    if (!isJudgmentAgainstOrganizations) {
      formRef?.current.change(`${judgmentItemSubjectDataItemName}.judgmentAgainstOrganizations` as keyof IKnmErknmForm);
    }
  }, [isJudgmentAgainstOrganizations]);

  useEffect(() => {
    if (!isJudgmentAgainstOfficialPerson) {
      formRef?.current.change(
        `${judgmentItemSubjectDataItemName}.judgmentAgainstOfficialPerson` as keyof IKnmErknmForm,
      );
    }
  }, [isJudgmentAgainstOfficialPerson]);

  const renderJudgmentAgainstOfficialPersonItem = () => {
    return (
      <FieldArray name={`${judgmentItemSubjectDataItemName}.judgmentAgainstOfficialPerson`}>
        {({ fields }: FieldArrayRenderProps<IKnmErknmFormJudgmentAgainstInfo, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          return fields.map((judgmentAgainstName, judgmentAgainstOfficialPersonNameIndex) => {
            const judgmentAgainstValue: IKnmErknmFormJudgmentAgainstInfo =
              fields.value?.[judgmentAgainstOfficialPersonNameIndex];
            const judgmentAgainstOfficialPersonId = getJudgmentAgainstOfficialPersonBlockId(
              judgmentItemSubjectDataItemId,
              judgmentAgainstValue.listId,
            );

            const hasError = checkHasErrorInCollapseBlock(formRef, judgmentAgainstName);

            return (
              <KnmCollapse
                id={judgmentAgainstOfficialPersonId}
                theme="fields"
                title={'Должностное лицо'}
                isNew={judgmentAgainstValue.isNew}
                isNewTitle={'Новое'}
                opened={true}
                onClose={
                  !watchOnly
                    ? makeRemoveJudgmentAgainstOfficialPersonHandler(judgmentAgainstOfficialPersonNameIndex)
                    : undefined
                }
                key={judgmentAgainstName}
                changeOpenedCallback={e =>
                  setKnmCollapseOpened(state => ({
                    ...state,
                    [judgmentAgainstName]: e,
                  }))
                }
                error={hasError && knmCollapseOpened[judgmentAgainstName]}
              >
                <>
                  {isJudgmentAgainstOfficialPerson && (
                    <Row>
                      <FieldBlock id={`${judgmentAgainstOfficialPersonId}.officialPersonFio`} size="half">
                        <TextInputField
                          name={`${judgmentAgainstName}.officialPersonFio`}
                          disabled={watchOnly}
                          placeholder="Ф.И.О"
                        />
                      </FieldBlock>
                      <FieldBlock id={`${judgmentAgainstOfficialPersonId}.officialPersonJobTitle`} size="half">
                        <TextInputField
                          name={`${judgmentAgainstName}.officialPersonJobTitle`}
                          disabled={watchOnly}
                          placeholder="Должность"
                        />
                      </FieldBlock>
                    </Row>
                  )}
                  <ErknmJudgmentAgainstInfo
                    judgmentItemAgainstInfoId={`${judgmentAgainstOfficialPersonId}`}
                    judgmentItemAgainstInfoName={judgmentAgainstName}
                    judgmentItemAgainstValue={judgmentAgainstValue}
                    watchOnly={watchOnly}
                  />
                </>
              </KnmCollapse>
            );
          });
        }}
      </FieldArray>
    );
  };

  return (
    <>
      {judgmentItemValue.judgmentAction === JudgmentActionsEnum.ADD_JUDGMENT && (
        <>
          <Row>
            <FieldBlock id={`${judgmentItemSubjectDataItemId}.isJudgmentAgainstOrganizations`}>
              <CheckboxField
                name={`${judgmentItemSubjectDataItemName}.isJudgmentAgainstOrganizations`}
                checked={isJudgmentAgainstOrganizations}
                label="Судебный акт принят в отношении контролируемого лица"
                type="checkbox"
                theme="system"
                disabled={watchOnly}
              />
            </FieldBlock>
          </Row>
          {isJudgmentAgainstOrganizations && (
            <ErknmJudgmentAgainstInfo
              judgmentItemAgainstInfoId={`${judgmentItemSubjectDataItemId}.judgmentAgainstOrganizations`}
              judgmentItemAgainstInfoName={`${judgmentItemSubjectDataItemName}.judgmentAgainstOrganizations`}
              judgmentItemAgainstValue={judgmentAgainstOrganizations}
              watchOnly={watchOnly}
            />
          )}
          <Row notSpace notWrap>
            <FieldBlock id={`${judgmentItemSubjectDataItemId}.isJudgmentAgainstOfficialPerson`} size="content">
              <CheckboxField
                name={`${judgmentItemSubjectDataItemName}.isJudgmentAgainstOfficialPerson`}
                checked={isJudgmentAgainstOfficialPerson}
                label="Судебный акт принят в отношении должностного лица"
                type="checkbox"
                theme="system"
                disabled={watchOnly}
              />
            </FieldBlock>
            {isJudgmentAgainstOfficialPerson && (
              <FieldBlock size="content" id={`${judgmentItemSubjectDataItemId}.judgmentAgainstOfficialPerson`}>
                <Button
                  className="shared-btn-add"
                  text="Добавить"
                  type="button"
                  variant="link"
                  color="primary"
                  disabled={watchOnly}
                  onClick={addJudgmentAgainstOfficialPerson}
                />
              </FieldBlock>
            )}
          </Row>
          {isJudgmentAgainstOfficialPerson && !judgmentAgainstOfficialPerson?.length && (
            <p className="shared-input-error">Поле не может быть пустым</p>
          )}
          {isJudgmentAgainstOfficialPerson && renderJudgmentAgainstOfficialPersonItem()}
        </>
      )}
      {(judgmentItemValue.judgmentAction === JudgmentActionsEnum.ANNULMENT_AKT ||
        judgmentItemValue.judgmentAction === JudgmentActionsEnum.ANNULMENT_JUDGMENT) && (
        <>
          <AddJudgmentModal
            formRef={formRef}
            visible={isModalVisible}
            modalType={'actsAndDecisions'}
            judgmentItemName={judgmentItemName}
            judgmentItemSubjectDataItemName={judgmentItemSubjectDataItemName}
            onSave={saveModal}
            onCancel={cancelModal}
            acts={formRef?.current
              .getFieldState('acts')
              ?.value?.filter(
                x => x.organization?.guid === organizationGuid || x.organizationTemplate?.value === organizationGuid,
              )}
            decisions={formRef?.current
              .getFieldState('resultDecisions')
              ?.value?.filter(
                x => x.organization?.guid === organizationGuid || x.organizationTemplate?.value === organizationGuid,
              )}
          />
          <Section
            title="Сведения об актах (решениях) в рамках КНМ, которые отменены или изменены"
            type="list"
            id={getJudgmentCancellationInfoBlockId(judgmentItemSubjectDataItemId)}
            customButton={
              <Button
                className="shared-btn-add"
                text="Добавить"
                type="button"
                variant="link"
                color="primary"
                disabled={watchOnly}
                onClick={openModal}
              />
            }
          >
            {!cancellationActsAndDecisions?.length && <p className="shared-input-error">Поле не может быть пустым</p>}
            {cancellationActsAndDecisions && (
              <ErknmJudgmentActAndDecisionInfo
                watchOnly={watchOnly}
                judgmentItemName={judgmentItemName}
                judgmentItemSubjectDataItemName={judgmentItemSubjectDataItemName}
                judgmentItemSubjectDataItemId={judgmentItemSubjectDataItemId}
              />
            )}
          </Section>
        </>
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState): IErknmJudgmentItemSubjectDataItemStoreProps => ({
  formRef: getKnmFormRef(state),
});

export const ErknmJudgmentItemSubjectDataItem = connect(mapStateToProps)(ErknmJudgmentItemSubjectDataItemWithoutStore);
